import * as React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { toast } from '@/lib/toasts/api'
import { TaskType } from '@/app/constants/taskType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import { useScopeBusy } from '@/modules/shared/hooks/useScopeBusy'

type Options = {
  companyId: string | undefined
  /**
   * Issues the request. Every caller keeps its own service wrapper — two of the three
   * screens go through `useViolationService`, which also drives the global spinner,
   * and dropping that here would silently change their behaviour.
   */
  start: (requestId: string) => Promise<unknown>
}

/**
 * Recalculating violations, as a tracked task.
 *
 * The three screens that offer this (driver violations page, the row menu in the new
 * violations table, and the violation settings tab) all hit the same endpoint with the
 * same company-wide scope, so a second run while one is in flight is refused with 409.
 * They used to report that as "Błąd przeliczania naruszeń" — an error the user could
 * do nothing about, for work that was in fact already happening. Here the running task
 * is adopted instead.
 *
 * Unlike the timesheet case, adoption is honest without caveats: the scope is the whole
 * company and the calculation always covers every active driver, so the running task
 * does exactly what this click asked for.
 */
export const useViolationCalculationTask = ({ companyId, start }: Options) => {
  const { t } = useLocalize()
  const scopeKey = companyId ? taskScopeKey.violations(companyId) : null
  const scopeBusy = useScopeBusy(scopeKey)

  const calculate = React.useCallback(async () => {
    if (!companyId) return false

    // Jeden identyfikator dla zadania i dla samego zadania HTTP - serwer zaklada
    // ImportTask pod tym ID, wiec rozjazd oznaczalby sledzenie nieistniejacego wpisu.
    const requestId = uuidv4()

    try {
      const { adopted, startedBy } = await startTrackedTask({
        companyId,
        type: TaskType.Violation,
        taskId: requestId,
        scopeKey: taskScopeKey.violations(companyId),
        start: () => start(requestId),
      })

      toast.info({
        title: adopted
          ? t('driverViolations.calculation-in-progress')
          : t('driverViolations.force-calculation-success'),
        text: adopted
          ? startedBy
            ? `${startedBy}: ${t('driverViolations.calculation-already-running')}`
            : t('driverViolations.calculation-already-running')
          : t('driverViolations.force-calculation-confirmation'),
      })
      return true
    } catch (error) {
      if (isTaskRateLimited(error)) {
        toast.warning({
          title: t('driverViolations.calculation-in-progress'),
          text: t('timesheet.too-many-tasks'),
        })
        return false
      }
      console.error('Error calculating violations:', error)
      toast.error({ text: t('driverViolations.calculated-error') })
      return false
    }
  }, [companyId, start, t])

  return { calculate, scopeBusy }
}
